import { injectable } from 'inversify';
import React from 'react';
import { Observer } from '../../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../../concepts/objects';
import { Icon } from '../../../../concepts/resources/Icon';
import { useBurden } from '../../../../platform/Hook';
import { useResolve } from '../../../Container';
import { EditorSession } from '../../../canvas/EditorSession';
import { SelectedVoteRound } from '../SelectedVoteRound';
import { VoteIndex } from '../VoteIndex';
import { VoteRound } from '../VoteRound';
import { gotoRound } from '../VotingCommands';
import { resolveActiveVoteRound } from '../VotingHelpers';
import { CoolReadStore } from '../VotingViewState';
import { ActionItem, ActionLayout, Box } from './../../../../ui';
import { CreateVoteRoundActionItemWithPopper } from './CreateVoteRoundPanel';
import { ShowVoteRankingMenu } from './ShowVoteRankingMenu';
import { VoteRoundInfoActionItemWithPopper } from './VoteRoundInfoPanel';

const initialState = {
    hasRounds: false,
    label: '',
    canGotoPrev: false,
    canGotoNext: false,
    canCreateNew: false,
};

@injectable()
class VotingWidgetViewModel extends CoolReadStore(initialState) {

    constructor(
        private objects: ObjectManager,
        private session: EditorSession,
        private voteIndex: VoteIndex) {
        super();

        this.burden(
            objects.on('VoteRound', 'allocated', this.doUpdate),
            objects.on('VoteRound', 'affected', this.doUpdate),
            objects.on('VoteRound', 'released', this.doUpdate),
            voteIndex.on('updated', this.doUpdate),
            session.observe(SelectedVoteRound, this.doUpdate),
        );

        this.doUpdate();
    }

    private doUpdate = () => {
        const { objects, session, voteIndex, } = this;
        const allRounds = VoteRound.sort(objects.getAll(VoteRound));
        const activeRound = resolveActiveVoteRound(objects, session);
        this.mutate(x => {
            x.hasRounds = allRounds.length > 0;
            x.label = activeRound?.label ?? '';
            x.canGotoPrev = activeRound && allRounds.indexOf(activeRound) > 0;
            x.canGotoNext = activeRound && allRounds.indexOf(activeRound) !== allRounds.length - 1;
            x.canCreateNew = !activeRound || voteIndex.count(activeRound.id, '*') > 0;
        });
    };
}

export const VotingWidget = () => {

    const dispatch = useResolve(CommandDispatcher);
    const objects = useResolve(ObjectManager);
    const session = useResolve(EditorSession);
    const voteIndex = useResolve(VoteIndex);

    const viewModel = useBurden(() => new VotingWidgetViewModel(objects, session, voteIndex));

    const goPrev = () => dispatch(gotoRound, [-1]);
    const goNext = () => dispatch(gotoRound, [+1]);

    return <Observer store={viewModel} render={x => {
        return <Box flex width="18" style={{ flexDirection: 'column' }}>
            <ActionLayout lines padding={2}>
                {x.hasRounds && <>
                    <ActionItem disabled={!x.canGotoPrev} pressable onClick={goPrev}>
                        <Icon size="1" src="deqo://icons/deqo/chevron-left" />
                    </ActionItem>
                    <VoteRoundInfoActionItemWithPopper label={x.label} />
                    {x.canGotoNext &&
                        <ActionItem pressable onClick={goNext}>
                            <Icon size="1" src="deqo://icons/deqo/chevron-right" />
                        </ActionItem>
                    }
                    {!x.canGotoNext && <CreateVoteRoundActionItemWithPopper mode="icon" disabled={!x.canCreateNew} />}
                    <ShowVoteRankingMenu />
                </>}
                {!x.hasRounds && <>
                    <CreateVoteRoundActionItemWithPopper mode="label" />
                </>}
            </ActionLayout>
        </Box>;
    }} />;
};
